import { isApiError, type TemplateSize } from './api'
import type { Lang } from '../i18n'

/**
 * The template size limits, checked before a file is sent (AB#193) — the same numbers as
 * backend/domain/template-size.ts, which refuses with a JSON 413 if one gets through anyway. A
 * PowerPoint template may be SENT larger than a Word one: the server drops its sample slides and
 * keeps only its masters, layouts, theme and media.
 */
const MB = 1024 * 1024
export const MAX_TEMPLATE_UPLOAD_BYTES = { word: 15 * MB, slides: 50 * MB } as const

export type UploadedTemplate = keyof typeof MAX_TEMPLATE_UPLOAD_BYTES

/** The refusal for a file over its component's limit, or null when it may be sent. */
export function templateSizeOf(component: UploadedTemplate, bytes: number): TemplateSize | null {
  const limit = MAX_TEMPLATE_UPLOAD_BYTES[component]
  return bytes > limit ? { stage: 'upload', bytes, limit } : null
}

/**
 * The size refusal a failed upload carries: the server's own numbers on its `too_large` 413. Null
 * for anything else — including a 413 without numbers (a proxy, or the request body itself refused),
 * which the caller words as a plain "too large".
 */
export function templateSizeFromError(e: unknown): TemplateSize | null {
  if (!isApiError(e) || e.status !== 413) return null
  const { stage, bytes, limit } = e.details ?? {}
  if ((stage === 'upload' || stage === 'kept' || stage === 'expanded') && typeof bytes === 'number' && typeof limit === 'number') {
    return { stage, bytes, limit }
  }
  return null
}

/** `42 247 410` → `40.3 MB` (`40,3 Mo` in French); whole megabytes lose the decimal. */
export function formatMegabytes(bytes: number, lang: Lang): string {
  const n = Math.round((bytes / MB) * 10) / 10
  return lang === 'fr' ? `${String(n).replace('.', ',')} Mo` : `${n} MB`
}
